import { useEffect, useState } from 'react';
import { Users, AppWindow, ClipboardCheck, TrendingUp, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useI18n } from '@/i18n';
import { getDashboardStats, type DashboardStats } from '@/api/dashboard';
import { useToast } from '@/hooks/use-toast';

export default function AdminDashboard() {
  const { t } = useI18n();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getDashboardStats();
        setStats(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load dashboard';
        toast({
          title: 'Error',
          description: message,
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [toast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const statCards = [
    {
      label: t.admin.totalUsers,
      value: stats?.totalUsers ?? 0,
      subtext: t.admin.activeUsers,
      icon: Users,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      label: t.admin.totalCreators,
      value: stats?.totalCreators ?? 0,
      subtext: t.admin.canSubmitApps,
      icon: TrendingUp,
      color: 'bg-green-100 text-green-600',
    },
    {
      label: t.admin.pendingReviews,
      value: stats?.pendingReviews ?? 0,
      subtext: t.admin.needsAction,
      icon: ClipboardCheck,
      color: 'bg-yellow-100 text-yellow-600',
    },
    {
      label: t.admin.publishedApps,
      value: stats?.publishedApps ?? 0,
      subtext: t.admin.visibleToUsers,
      icon: AppWindow,
      color: 'bg-purple-100 text-purple-600',
    },
  ];

  const recentRequests = stats?.recentRequests ?? [];

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t.admin.dashboard}</h1>
        <p className="text-gray-500 mt-1">{t.admin.overview}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <Card key={stat.label}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  <p className="text-sm text-gray-500 mt-1">{stat.subtext}</p>
                </div>
                <div className={`p-3 rounded-xl ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t.admin.pendingRequests}</CardTitle>
          </CardHeader>
          <CardContent>
            {recentRequests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ClipboardCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{t.admin.noPendingRequests}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentRequests.map((request) => (
                  <div
                    key={request.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{request.appName}</p>
                      <p className="text-sm text-gray-500">
                        {request.type === 'create' && t.requestTypes.create}
                        {request.type === 'update' && t.requestTypes.update}
                        {request.type === 'delete' && t.requestTypes.delete}
                        {' · '}
                        {request.creatorEmail}
                      </p>
                    </div>
                    <span className="px-2.5 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                      {t.status.pending}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t.admin.quickActions}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <a
                href="/admin/users"
                className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <Users className="w-8 h-8 text-primary-600 mb-2" />
                <span className="text-sm font-medium text-gray-900">{t.admin.userManagement}</span>
              </a>
              <a
                href="/admin/reviews"
                className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <ClipboardCheck className="w-8 h-8 text-primary-600 mb-2" />
                <span className="text-sm font-medium text-gray-900">{t.admin.appReview}</span>
              </a>
              <a
                href="/admin/apps"
                className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <AppWindow className="w-8 h-8 text-primary-600 mb-2" />
                <span className="text-sm font-medium text-gray-900">{t.admin.publishedAppsTitle}</span>
              </a>
              <a
                href="/admin/settings"
                className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <TrendingUp className="w-8 h-8 text-primary-600 mb-2" />
                <span className="text-sm font-medium text-gray-900">{t.admin.settings}</span>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
